import {
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Pill,
  Stack,
  Text,
  Title,
  Tooltip
} from '@mantine/core'
import { IconCpu2, IconRefresh } from '@tabler/icons-react'
import { keyBy } from 'lodash-es'
import { ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { OnlineStatus } from '@/components/devices/OnlineStatus/OnlineStatus'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import {
  DeploymentPlan,
  DeploymentPlanDevice,
  DeploymentPlanGroup,
  DeploymentPlanMember,
  DeploymentPlanSingleCamera
} from '@/types/deployment'
import { Device } from '@/types/device'
import { getDeviceGpus } from '@/utils/device/getDeviceAttr'
import { getPlanErrorKind } from '../../helpers/planError'
import { getGroupExclusionReasonIds } from './groupExclusionReasons'

const GROUP_ACTIONS = ['keep', 'update', 'create', 'remove']
const MEMBER_CHANGES = [
  'unchanged',
  'updated',
  'added',
  'moved_in',
  'moved_out',
  'removed'
]
const CREATED_BECAUSE = [
  'first_group',
  'groups_full',
  'frame_size_bucket',
  'milestone_server',
  'settings_changed'
]
const NOT_SUPPORTED_REASONS = [
  'groups_disabled',
  'app_switch_off',
  'device_not_capable',
  'jetson_device',
  'no_group_image',
  'driver_too_old'
]
const DEVICE_WARNINGS = [
  'gpu_load_high',
  'groups_exceed_gpu_memory',
  'no_allowed_gpu',
  'gpu_shared_with_vlm',
  'gpu_memory_estimate_exceeds_capacity'
]

const warningMessageId = (
  warning: string,
  planDevice: Pick<DeploymentPlanDevice, 'load' | 'memory'>
) => {
  const code = knownOrUnknown(warning, DEVICE_WARNINGS)
  const missingFigure =
    (code === 'gpu_load_high' && !planDevice.load) ||
    (code === 'groups_exceed_gpu_memory' && !planDevice.memory)

  return `deployments.plan.device.warning.${code}${missingFigure ? '.noFigure' : ''}`
}

const ACTION_COLORS: Partial<Record<string, string>> = {
  keep: 'gray',
  update: 'blue',
  create: 'green',
  remove: 'red'
}

const CHANGE_COLORS: Partial<Record<string, string>> = {
  unchanged: 'gray',
  updated: 'blue',
  added: 'green',
  moved_in: 'green',
  moved_out: 'orange',
  removed: 'red'
}

const knownOrUnknown = (value: string, known: string[]) =>
  known.includes(value) ? value : 'unknown'

const ReasonBadge = ({ reason }: { reason: string }) => {
  const { labelId, hintId } = getGroupExclusionReasonIds(reason)

  return (
    <Tooltip
      label={<FormattedMessage id={hintId} values={{ reason }} />}
      w={300}
      multiline
    >
      <Badge size="xs" variant="light" color="gray" style={{ cursor: 'help' }}>
        <FormattedMessage id={labelId} />
      </Badge>
    </Tooltip>
  )
}

type CameraListProps = {
  titleId: string
  cameras: DeploymentPlanSingleCamera[]
  hint?: ReactNode
}

const CameraList = ({ titleId, cameras, hint }: CameraListProps) => (
  <Stack gap={4}>
    <Text size="sm" fw={600}>
      <FormattedMessage id={titleId} values={{ count: cameras.length }} />
    </Text>

    {hint && (
      <Text size="xs" c="dimmed">
        {hint}
      </Text>
    )}

    {cameras.map((camera) => (
      <Group key={camera.stream_id} gap="xs" wrap="nowrap">
        <Text size="sm">
          <span>{camera.camera_name || camera.stream_id}</span>
        </Text>

        {camera.reasons.map((reason) => (
          <ReasonBadge key={reason} reason={reason} />
        ))}
      </Group>
    ))}
  </Stack>
)

const MemberRow = ({ member }: { member: DeploymentPlanMember }) => {
  const released = member.stream_index === null

  return (
    <Group gap="xs" wrap="nowrap">
      <Text
        size="sm"
        c={released ? 'dimmed' : undefined}
        td={released ? 'line-through' : undefined}
      >
        <span>{member.camera_name || member.stream_id}</span>
      </Text>

      <Badge
        size="xs"
        variant="light"
        color={CHANGE_COLORS[member.change] ?? 'gray'}
      >
        <FormattedMessage
          id={`deployments.plan.member.change.${knownOrUnknown(member.change, MEMBER_CHANGES)}`}
          values={{ change: member.change }}
        />
      </Badge>
    </Group>
  )
}

const GroupCard = ({ group }: { group: DeploymentPlanGroup }) => {
  const { settings } = group
  const memberCount = group.members.filter(
    (member) => member.stream_index !== null
  ).length

  return (
    <Card padding="sm" radius="sm" withBorder>
      <Group gap="xs" wrap="nowrap">
        <Text size="sm" fw={600}>
          {group.display_number === null ? (
            <FormattedMessage id="deployments.plan.group.new" />
          ) : (
            <FormattedMessage
              id="deployments.plan.group.title"
              values={{ number: group.display_number }}
            />
          )}
        </Text>

        <Badge
          size="sm"
          variant="light"
          color={ACTION_COLORS[group.action] ?? 'gray'}
        >
          <FormattedMessage
            id={`deployments.plan.group.action.${knownOrUnknown(group.action, GROUP_ACTIONS)}`}
            values={{ action: group.action }}
          />
        </Badge>

        {group.tracked && (
          <Badge size="sm" variant="light" color="teal">
            <FormattedMessage id="deployments.plan.group.tracked" />
          </Badge>
        )}

        <Text size="xs" c="dimmed">
          <FormattedMessage
            id="deployments.plan.group.capacity"
            values={{ members: memberCount, capacity: group.capacity }}
          />
        </Text>
      </Group>

      {group.created_because && (
        <Text size="xs" c="dimmed" mt={2}>
          <FormattedMessage
            id={`deployments.plan.group.createdBecause.${knownOrUnknown(group.created_because, CREATED_BECAUSE)}`}
            values={{ reason: group.created_because }}
          />
        </Text>
      )}

      <Group gap={6} mt="xs">
        <Pill size="xs">
          <FormattedMessage
            id="deployments.plan.group.precision"
            values={{ precision: settings.precision.toUpperCase() }}
          />
        </Pill>

        <Pill size="xs">
          {settings.interval === 0 ? (
            <FormattedMessage id="deployments.plan.group.everyFrame" />
          ) : (
            <FormattedMessage
              id="deployments.plan.group.everyNthFrame"
              values={{ n: settings.interval + 1 }}
            />
          )}
        </Pill>

        <Pill size="xs">
          <FormattedMessage
            id="deployments.plan.group.frameSize"
            values={{
              width: settings.frame_width,
              height: settings.frame_height
            }}
          />
        </Pill>

        <Pill size="xs">
          <FormattedMessage
            id="deployments.plan.group.clip"
            values={{ pre: settings.pre_event_s, post: settings.post_event_s }}
          />
        </Pill>
      </Group>

      {group.restarts_container && (
        <PlatformAlert variant="warning" mt="xs" compact>
          <FormattedMessage id="deployments.plan.group.restarts" />
        </PlatformAlert>
      )}

      <Stack gap={4} mt="xs">
        {group.members.map((member) => (
          <MemberRow key={member.stream_id} member={member} />
        ))}
      </Stack>
    </Card>
  )
}

type DeviceCardProps = {
  planDevice: DeploymentPlanDevice
  device?: Device
}

const DeviceCard = ({ planDevice, device }: DeviceCardProps) => {
  const intl = useIntl()
  const gpus = device ? getDeviceGpus(device) : []

  const perGpuText = (values: Record<string, number>, unit: string) =>
    Object.entries(values)
      .map(([index, value]) =>
        intl.formatMessage(
          { id: 'deployments.plan.device.perGpu' },
          { index, value: `${value}${unit}` }
        )
      )
      .join(', ')

  const hasLiveGroups = planDevice.groups.some(
    (group) => group.action !== 'remove'
  )

  const gpuNames = planDevice.allowed_gpus.map((index) => {
    const gpu = gpus.find((candidate) => candidate.index === index)

    return gpu
      ? intl.formatMessage(
          { id: 'devices.gpu.indexedName' },
          { index, name: gpu.name }
        )
      : intl.formatMessage({ id: 'devices.gpu.indexLabel' }, { index })
  })

  return (
    <Card padding="md" radius="md" withBorder>
      <Stack gap="sm">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs" wrap="nowrap">
            <IconCpu2 size={18} />

            <Text size="sm" fw={600}>
              <span>
                {device?.label || device?.name || planDevice.device_id}
              </span>
            </Text>

            {device && (
              <OnlineStatus status={device.active ? 'online' : 'offline'} />
            )}
          </Group>

          {planDevice.groups_supported && (
            <Badge size="sm" variant="light" color="green">
              <FormattedMessage id="deployments.plan.device.groupsSupported" />
            </Badge>
          )}
        </Group>

        {!planDevice.groups_supported && (
          <Text size="sm">
            <FormattedMessage
              id={`deployments.plan.device.notSupported.${knownOrUnknown(planDevice.not_supported_reason ?? '', NOT_SUPPORTED_REASONS)}`}
              values={{ reason: planDevice.not_supported_reason }}
            />
          </Text>
        )}

        {gpuNames.length > 0 && (
          <Text size="xs" c="dimmed">
            <FormattedMessage
              id="deployments.plan.device.allowedGpus"
              values={{ gpus: <span>{gpuNames.join(', ')}</span> }}
            />
          </Text>
        )}

        {hasLiveGroups && planDevice.load && (
          <Text size="xs" c="dimmed">
            <FormattedMessage
              id="deployments.plan.device.load"
              values={{
                pct: planDevice.load.pct,
                perGpu: perGpuText(planDevice.load.per_gpu, '%')
              }}
            />
          </Text>
        )}

        {hasLiveGroups && planDevice.memory && (
          <Text size="xs" c="dimmed">
            <FormattedMessage
              id="deployments.plan.device.memory"
              values={{
                fit: planDevice.memory.fit,
                needed: planDevice.memory.needed,
                perGpu: perGpuText(planDevice.memory.per_gpu, ''),
                estimate: planDevice.memory.estimate_mb
              }}
            />
          </Text>
        )}

        {planDevice.warnings.map((warning) => (
          <PlatformAlert key={warning} variant="warning" compact>
            <FormattedMessage
              id={warningMessageId(warning, planDevice)}
              values={{
                code: warning,
                pct: planDevice.load?.pct,
                fit: planDevice.memory?.fit,
                needed: planDevice.memory?.needed
              }}
            />
          </PlatformAlert>
        ))}

        {planDevice.groups.map((group, index) => (
          <GroupCard key={group.group_id ?? `new-${index}`} group={group} />
        ))}

        {planDevice.single_camera.length > 0 && (
          <CameraList
            titleId="deployments.plan.single.title"
            cameras={planDevice.single_camera}
          />
        )}

        {planDevice.paused.length > 0 && (
          <CameraList
            titleId="deployments.plan.paused.title"
            cameras={planDevice.paused}
            hint={<FormattedMessage id="deployments.plan.paused.hint" />}
          />
        )}
      </Stack>
    </Card>
  )
}

type DeployPlanSectionProps = {
  plan?: DeploymentPlan
  isLoading: boolean
  error: unknown
  devices?: Device[]
  onRefresh: () => void
}

export const DeployPlanSection = ({
  plan,
  isLoading,
  error,
  devices,
  onRefresh
}: DeployPlanSectionProps) => {
  const errorKind = getPlanErrorKind(error)
  const deviceById = keyBy(devices, 'id')

  if (!errorKind && (!plan ? !isLoading : !plan.groups_enabled)) {
    return null
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <div>
          <Title order={5}>
            <FormattedMessage id="deployments.plan.title" />
          </Title>

          <Text size="sm" c="dimmed">
            <FormattedMessage id="deployments.plan.description" />
          </Text>
        </div>

        <Button
          variant="subtle"
          size="xs"
          leftSection={<IconRefresh size={14} />}
          loading={isLoading}
          onClick={onRefresh}
        >
          <FormattedMessage id="deployments.plan.refresh" />
        </Button>
      </Group>

      {errorKind === 'invalid' && (
        <PlatformAlert
          variant="error"
          title={<FormattedMessage id="deployments.plan.invalid" />}
        >
          {extractErrorMessage(error, undefined, {
            includeFieldNameInValidationErrors: false
          })}
        </PlatformAlert>
      )}

      {errorKind === 'unavailable' && (
        <PlatformAlert variant="warning">
          <FormattedMessage id="deployments.plan.unavailable" />
        </PlatformAlert>
      )}

      {!errorKind && !plan && (
        <Group gap="xs">
          <Loader size="xs" />

          <Text size="sm" c="dimmed">
            <FormattedMessage id="deployments.plan.loading" />
          </Text>
        </Group>
      )}

      {!errorKind && plan && (
        <>
          <Text size="sm">
            <FormattedMessage
              id="deployments.plan.summary"
              values={plan.summary}
            />
          </Text>

          {plan.devices.map((planDevice) => (
            <DeviceCard
              key={planDevice.device_id}
              planDevice={planDevice}
              device={deviceById[planDevice.device_id]}
            />
          ))}
        </>
      )}
    </Stack>
  )
}
